<template>
<div v-if="receipt.id">

  <div class="box">
    <div class="flex flex-wrap">
      <div class="w-full md:w-1/2">
        <img class="h-10" :src="!config.logoPrintPath || url.resolve(config.URL.API.host, config.logoPrintPath)">
      </div>
      <div class="w-full md:w-1/2 md:text-right">
        <p
          class="font-bold uppercase"
          :class="receipt.type === 'purchases' ? 'text-red-500' : 'text-green-500'"
        >{{ receipt.type.slice(0, -1) }}</p>

        <div class="flex items-center justify-end">
          <p class="text-2xl font-bold">
            Receipt
          </p>
        </div>
      </div>
    </div>

    <div class="flex flex-wrap">
      <div class="w-full md:w-8/12" v-if="receipt.contact">
        <div class="mt-3 pb-3 border-b border-theme-200 light:border-theme-600">
          <a class="text-link" @click.stop="$router.push($contactPageByType(receipt.contact, 'details'))">
            <span class="mb-0 text-lg">
              {{ receipt.contact.first_name }} {{ receipt.contact.last_name }}
              <template v-if="receipt.contact.company_name"> ({{ receipt.contact.company_name }})</template>
              <small>#{{ receipt.contact.id }}</small>
            </span>
          </a>
        </div>

        <div class="flex flex-wrap text-sm mt-2">
          <div class="w-full md:w-1/2">
            <div v-for="(addr, i) in receipt.contact.addresses" :key="'address-' + i">
              <p class="text-lg font-bold">Address</p>
              <address>
                {{ addr.address_1 }},<br>
                {{ addr.city }},
                <span v-if="addr.state">{{ addr.state }}, </span>
                <span>{{ addr.post_code }}</span>,<br>
                <span>{{ CountryByID(addr.country_id).name }}</span>
              </address>
            </div>
          </div>
          <div class="w-full md:w-1/2">
            <p class="text-lg font-bold">Phone</p>
            <p class="font-bold" v-for="(phone, i) in receipt.contact.phone_numbers" :key="'phone-' + i">
              <template v-if="phone.countryData = CountryByID(phone.country_id)">
                <span :title="phone.countryData.name">{{ '+' + phone.countryData.calling_code }}</span> {{ phone.number }}
              </template>
            </p>
          </div>
        </div>
      </div>

      <div class="w-full md:w-4/12 flex flex-col">
        <div class="self-end pb-3 border-b border-theme-200 light:border-theme-600 text-sm">
          <table>
            <tbody>
              <tr>
                <td colspan="2" class="font-bold text-lg text-right">#{{ receipt.id }}</td>
              </tr>
              <tr>
                <td>Date:</td>
                <td class="text-right">
                  {{ $moment(receipt.created_at).format(config.dateFormats.display) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="text-sm text-right mt-2">
          <p class="text-base font-bold">{{ config.companyName }}</p>
          <p>{{ config.companyAddress }}, {{ config.companyCity }}</p>
          <p v-if="config.companyState">{{ config.companyState }}, </p>
          <p>{{ config.companyPostCode }}, {{ config.companyCountry }}</p>
          <p>{{ config.companyPhone }}</p>
          <p v-if="config.companyVATNumber"><strong>VAT:</strong> {{ config.companyVATNumber }}</p>
          <p v-if="config.companyIBAN"><strong>IBAN:</strong> {{ config.companyIBAN }}</p>
          <p v-if="config.companyAccountNumber"><strong>Account No:</strong> {{ config.companyAccountNumber }}</p>
        </div>
      </div>
    </div>

    <div class="flex flex-wrap mt-4 -mx-1">
      <div class="w-full md:w-1/2 px-1">
        <div class="overflow-x-auto py-4 px-2 border border-theme-200 light:border-theme-600">
          <p class="font-bold">
            Created for
          </p>
          <table class="table w-full" v-if="createdFor.invoices.length">
            <thead>
              <tr>
                <th>#</th>
                <th>Contact</th>
                <th>Type</th>
                <th>Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(invoice, i) in createdFor.invoices" :key="'receipt-invoices-' + i">
                <td class="font-bold">{{ invoice.id }}</td>
                <td>
                  <a class="text-link" @click.stop="$router.push($contactPageByType(receipt.contact, 'details'))">
                    <span>
                      #{{ receipt.contact.id }}
                    </span>
                    {{ receipt.contact.first_name }}
                    {{ receipt.contact.last_name }}
                    <span v-if="receipt.contact.company_name">
                      ({{ receipt.contact.company_name }})
                    </span>
                  </a>
                </td>
                <td>
                  <p
                    class="font-bold text-xs text-white py-1 px-1 rounded text-center uppercase"
                    :class="invoice.type === 'purchases' ? 'bg-red-500' : 'bg-green-500'"
                  >{{ invoice.type }}</p>
                </td>
                <td>
                  <span
                    class="font-bold"
                    v-tooltip="{ content: `
                      <strong>Discount:</strong> ${$formatMoney(invoice.total.discount)}
                      <strong>Tax:</strong> ${$formatMoney(invoice.total.tax)}
                    ` }"
                  >
                    {{ $formatMoney(invoice.total.sub - invoice.total.discount + invoice.total.tax) }}
                  </span>
                </td>
                <td>
                  <div class="flex">
                    <a
                      class="hover:text-theme-darker text-base cursor-pointer p-2"
                      @click.stop="$router.push('/invoice/preview/' + invoice.id)"
                    ><i class="fas fa-info"></i></a>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <table class="table w-full" v-if="createdFor.credit_notes.length">
            <thead>
              <tr>
                <th>#</th>
                <th>Contact</th>
                <th>Type</th>
                <th>Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(creditNote, i) in createdFor.credit_notes" :key="'receipt-invoices-' + i">
                <td class="font-bold">{{ creditNote.id }}</td>
                <td>
                  <a class="text-link" @click.stop="$router.push($contactPageByType(creditNote.contact, 'details'))">
                    <span>
                      #{{ creditNote.contact.id }}
                    </span>
                    {{ creditNote.contact.first_name }}
                    {{ creditNote.contact.last_name }}
                    <span v-if="creditNote.contact.company_name">
                      ({{ creditNote.contact.company_name }})
                    </span>
                  </a>
                </td>
                <td>
                  <p
                    class="font-bold text-xs text-white py-1 px-1 rounded text-center uppercase"
                    :class="creditNote.type === 'purchases' ? 'bg-red-500' : 'bg-green-500'"
                  >{{ creditNote.type }}</p>
                </td>
                <td>
                  <span
                    class="font-bold"
                    v-tooltip="{ content: `
                      <strong>Discount:</strong> ${$formatMoney(creditNote.total.discount)}
                      <strong>Tax:</strong> ${$formatMoney(creditNote.total.tax)}
                    ` }"
                  >
                    {{ $formatMoney(creditNote.total.sub - creditNote.total.discount + creditNote.total.tax) }}
                  </span>
                </td>
                <td>
                  <div class="flex">
                    <a
                      class="hover:text-theme-darker text-base cursor-pointer p-2"
                      @click.stop="$router.push('/credit-note/preview/' + creditNote.id)"
                      v-if="!creditNoteID || creditNote.id !== creditNoteID"
                    ><i class="fas fa-info"></i></a>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="w-full md:w-1/2 px-1">
        <div class="flex flex-col">

          <div class="self-end text-lg border border-theme-200 light:border-theme-600">

            <p class="text-2xl font-bold text-center bg-theme py-1 mb-2">Payment</p>
            <div class="px-2">
              <table class="w-full">
                <tbody>
                  <tr>
                    <td>Method:</td>
                    <td class="font-bold text-right">{{ receipt.payment.method | sentenceCase }}</td>
                  </tr>
                  <tr>
                    <td>Amount:</td>
                    <td class="font-bold text-right">{{ $formatMoney(receipt.payment.value) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="text-lg mt-2 border-t border-theme-200 light:border-theme-600" v-if="receipt.payment.method === 'cheque'">
              <p class="text-2xl font-bold text-center bg-theme py-1 mb-2">Cheque</p>
              <div class="px-2">
                <table class="w-full">
                  <tbody>
                    <tr>
                      <td>Bank name:</td>
                      <td class="font-bold text-right">{{ receipt.payment.cheque.bank_name }}</td>
                    </tr>
                    <tr>
                      <td>Cheque number:</td>
                      <td class="font-bold text-right">{{ receipt.payment.cheque.cheque_number }}</td>
                    </tr>
                    <tr>
                      <td>Cheque date:</td>
                      <td class="font-bold text-right">{{ $moment(receipt.payment.cheque.cheque_date, 'DD/MM/YYYY').format(config.dateFormats.display) }}</td>
                    </tr>
                    <tr>
                      <td>Account number:</td>
                      <td class="font-bold text-right">{{ receipt.payment.cheque.account_number }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="text-lg mt-2 pb-2 border-t border-theme-200 light:border-theme-600 text-center" v-if="receipt.payment.note !== ''">
              <p class="text-2xl font-bold bg-theme py-1 mb-2">Note</p>
              <p class="px-2 text-sm">{{ receipt.payment.note }}</p>
            </div>

          </div>

        </div>
      </div>
    </div>

    <div class="flex flex-wrap flex-col mt-8">
      <div class="self-end text-right">
        <div class="btn px-3 py-2 btn-blue ml-3" @click="print">
          <i class="fas fa-print"></i>
          <span class="ml-2">Print</span>
        </div>
      </div>
    </div>
  </div>

</div>
</template>

<script>
import axios from 'axios'
import url from 'url'
import { mapGetters } from 'vuex'

var pdfMake = require('pdfmake/build/pdfmake.js')
var pdfFonts = require('pdfmake/build/vfs_fonts.js')
pdfMake.vfs = pdfFonts.pdfMake.vfs

export default {
  props: {
    paymentID: {},
    creditNoteID: {},
  },
  data() {
    return {
      url,
      receiptID: this.$route.params.receiptID,
      receipt: {},
    }
  },
  watch: {
    paymentID() {
      this.getByPayment(this.paymentID)
    },
  },
  computed: {
    createdFor() {
      let doc = {
        invoices: [],
        credit_notes: [],
      }
      if (this.receipt.invoices && this.receipt.invoices.length) {
        doc.invoices.push(...this.receipt.invoices)
      }
      if (this.receipt.credit_notes && this.receipt.credit_notes.length) {
        doc.credit_notes.push(...this.receipt.credit_notes)
      }
      if (this.receipt.payment.invoice) {
        doc.invoices.push(this.receipt.payment.invoice)
      }
      if (this.receipt.payment.credit_note) {
        doc.credit_notes.push(this.receipt.payment.credit_note)
      }
      // set totals
      for (let i in doc.invoices) {
        this.$setInvoiceTotals(doc.invoices[i])
      }
      for (let i in doc.credit_notes) {
        this.$setInvoiceTotals(doc.credit_notes[i])
      }
      return doc
    },
    ...mapGetters({
      config: 'config/config',
      CountryByID: 'countries/countryByID',
    })
  },
  mounted() {
    if (this.$route.params.receiptID) {
      this.receiptID = this.$route.params.receiptID
      this.get()
    } else if (!this.receiptID && this.paymentID) {
      this.getByPayment(this.paymentID)
    }
  },
  methods: {
    getByPayment(paymentID) {
      return new Promise((resolve, reject) => {
        axios.get('payments/receipt/' + paymentID)
          .then((response) => {
            const receipt = response.data

            this.receipt = Object.assign({}, receipt)
            resolve()
          })
          .catch((error) => {
            console.log(error.response)
            reject(error)
            this.$swal.fire({
              title: 'Receipt not found',
              type: 'warning',
            })
              .then(() => {
                // this.$router.push('/')
              })
          })
      })
    },
    get() {
      return new Promise((resolve, reject) => {
        axios.get('receipts/' + this.receiptID)
          .then((response) => {
            const receipt = response.data

            this.receipt = Object.assign({}, receipt)
            resolve()
          })
          .catch((error) => {
            console.log(error.response)
            reject(error)
            this.$swal.fire({
              title: 'Receipt not found',
              type: 'warning',
            })
              .then(() => {
                // this.$router.push('/')
              })
          })
      })
    },
    print() {
      // we could store logodata in data()
      // however loading the logo on each print request ensures latest logo usage
      this.invoiceLogoData()
        .then((data) => {
          this._print(data)
        })
    },

    invoiceLogoData() {
      return axios.get(this.config.logoPrintPath + '?_', {
        responseType: 'arraybuffer'
      })
        .then(response => {
          let image = btoa(
            new Uint8Array(response.data)
              .reduce((data, byte) => data + String.fromCharCode(byte), '')
          )
          return `data:${response.headers['content-type'].toLowerCase()};base64,${image}`
        })
    },
    _print(invoiceLogoData) {
      let addresses = {width: '*', text: ''}
      let addressList = []
      for (let i in this.receipt.contact.addresses) {
        let addr = this.receipt.contact.addresses[i]
        addressList.push(addr.address_1 + ',\n' + addr.city + ', ' + addr.state + ', ' + addr.post_code + ',\n' + this.CountryByID(addr.country_id).name)
      }
      addresses.text = addressList.join('\n')

      let phoneNumbers = {width: '*', text: ''}
      let phoneNumberList = []
      for (let i in this.receipt.contact.phone_numbers) {
        let phone = this.receipt.contact.phone_numbers[i]
        phoneNumberList.push('+' + phone.countryData.calling_code + ' ' + phone.number)
      }
      phoneNumbers.text = phoneNumberList.join('\n')

      let paymentDetails = [{ width: '*',text: '' }]
      if (this.receipt.payment.cheque && this.receipt.payment.cheque.bank_name !== '') {
        paymentDetails.push({
          width: 'auto',
          layout: {
            hLineWidth() {
              return 1
            },
            vLineWidth(i, node) {
              return (i === 0 || i === node.table.widths.length) ? 1 : 0
            },
            paddingLeft() { return 2 },
            paddingRight() { return 2 },
            paddingTop(i, node) {
              return (i === 0 || i === node.table.body.length) ? 10 : 2
            },
            paddingBottom(i, node) {
              return (i === 0 || i === node.table.body.length) ? 10 : 2
            },
          },
          table: {
            headerRows: 0,
            widths: ['auto', 'auto'],
            fontSize: 10,
            body: [
              [{text: 'Cheque details', fontSize:11}, ''],
              ['Bank name:', {text: this.receipt.payment.cheque.bank_name, bold: true}],
              ['Cheque number:', {text: this.receipt.payment.cheque.cheque_number, bold: true}],
              ['Cheque date:', {text: this.$moment(this.receipt.payment.cheque.cheque_date, 'DD/MM/YYYY').format(this.config.dateFormats.display), bold: true}],
              ['Account number:', {text: this.receipt.payment.cheque.account_number, bold: true}],
            ],
          },
          margin: [0, 0, 10, 0]
        })
      }
      paymentDetails.push({
        width: 'auto',
        layout: {
          hLineWidth() {
            return 1
          },
          vLineWidth(i, node) {
            return (i === 0 || i === node.table.widths.length) ? 1 : 0
          },
          paddingLeft() { return 10 },
          paddingRight() { return 10 },
          paddingTop() { return 10 },
          paddingBottom() { return 10 },
        },
        table: {
          headerRows: 0,
          widths: ['auto', 'auto'],
          body: [
            ['', {text: 'Payment', fontSize:11, alignment: 'right'}],
            ['Method:', {text: this.$options.filters.sentenceCase(this.receipt.payment.method), bold: true, alignment: 'right'}],
            ['Amount:', {text: this.$formatMoney(this.receipt.payment.value), bold: true, alignment: 'right'}],
          ],
        },
      })

      let companyVATText = [
        { text: '\nVAT: ', fontSize: 9, bold: true },
        { text: this.config.companyVATNumber, fontSize: 9 },
      ]

      let companyIBANText = [
        { text: '\nIBAN: ', fontSize: 9, bold: true },
        { text: this.config.companyIBAN, fontSize: 9 },
      ]

      let companyAccountNumberText = [
        { text: '\nAccount No: ', fontSize: 9, bold: true },
        { text: this.config.companyAccountNumber, fontSize: 9 },
      ]

      var docDefinition = {
        content: [
          {
            columns: [
              {
                width: 90,
                image: invoiceLogoData,
              },
              {
                width: '*',
                text: '',
              },
              {
                width: '25%',
                text: 'Receipt',
                fontSize: 20,
                bold: true,
                alignment: 'right'
              }
            ],
          },
          {
            columns: [
              {
                width: '*',
                text: '',
              },
              {
                width: '25%',
                text: [
                  '#' + this.receipt.id + '\n' + 'Date: ',
                  this.$moment(this.receipt.created_at).format(this.config.dateFormats.display),
                ],
                alignment: 'right'
              }
            ],
            margin: [ 0, 0, 0, 40 ],
          },
          {
            columns: [
              {
                text: [
                  {text: this.receipt.contact.first_name + ' ' + this.receipt.contact.last_name + (this.receipt.contact.company_name ? ' ' + this.receipt.contact.company_name : ''), bold: true, fontSize: 12},
                  {text: ' #' + this.receipt.contact.id},
                ],
              },
              {
                width: '*',
                text: '',
              },
            ],
          },
          {
            canvas: [ { type: 'line', x1: 0, y1: 0, x2: 515 - 25*515/100, y2: 0, lineWidth: 1 } ],
            margin: [ 0, 5, 0, 5 ]
          },
          {
            columns: [
              addresses,
              phoneNumbers,
              {
                width: '30%',
                text: [
                  { text: this.config.companyName + '\n', bold: true, fontSize: 10 },
                  { text: this.config.companyAddress + ' ' + this.config.companyCity + '\n', fontSize: 9 },
                  { text: (this.config.companyState ? this.config.companyState + ', ' : '') + ' ' + this.config.companyPostCode + ' ' + this.config.companyCountry + '\n', fontSize: 9 },
                  { text: this.config.companyPhone, fontSize: 9 },
                  ...this.config.companyVATNumber ? companyVATText : [],
                  ...this.config.companyIBAN ? companyIBANText : [],
                  ...this.config.companyAccountNumber ? companyAccountNumberText : [],
                ],
                alignment: 'right'
              }
            ],
            columnGap: 10
          },
          {
            canvas: [ { type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1 } ],
            margin: [ 0, 15, 0, 25 ]
          },
          {
            columns: paymentDetails,
            fontSize: 14,
            margin: [ 0, 0, 0, 20 ]
          },
          {
            columns: [
              {
                width: '35%',
                layout: {
                  hLineWidth(i, node) {
                    return (i === 1 || i === node.table.widths.length) ? 1 : 0
                  },
                  vLineWidth() {
                    return 0
                  },
                  hLineStyle: function () {
                    return {dash: {length: 5, space: 3}}
                  },
                  paddingLeft() { return 0 },
                  paddingRight() { return 10 },
                  paddingTop() { return 10 },
                  paddingBottom() { return 30 },
                },
                table: {
                  headerRows: 0,
                  widths: ['*'],
                  body: [
                    ['Issuer\'s signature'],
                  ],
                }
              },
              {
                width: '*',
                text: ''
              },
              {
                width: '35%',
                layout: {
                  hLineWidth(i, node) {
                    return (i === 1 || i === node.table.widths.length) ? 1 : 0
                  },
                  vLineWidth() {
                    return 0
                  },
                  hLineStyle: function () {
                    return {dash: {length: 5, space: 3}}
                  },
                  paddingLeft() { return 0 },
                  paddingRight() { return 10 },
                  paddingTop() { return 10 },
                  paddingBottom() { return 30 },
                },
                table: {
                  headerRows: 0,
                  widths: ['*'],
                  body: [
                    ['Receiver\'s signature'],
                  ],
                }
              },
            ],
          },
        ],
        footer(currentPage, pageCount) {
          if (pageCount === currentPage) {
            return {
              text: 'Thank you for doing business with us',
              alignment: 'center',
            }
          }
        },
        pageSize: 'A4',
        defaultStyle: {
          fontSize: 9,
        }
      }

      pdfMake.createPdf(docDefinition).open()
    },
  }
}
</script>
